import React, { useState, useEffect, useRef } from 'react';

const SwimmerLoader = ({ onReady, videoReady }) => {
  const [phase, setPhase] = useState('swimming'); // swimming → fadeOut → done
  const [minTimePassed, setMinTimePassed] = useState(false);
  const hasTriggered = useRef(false);

  useEffect(() => {
    // Minimum 2s so the animation feels intentional
    const timer = setTimeout(() => setMinTimePassed(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Fade out when BOTH: min time passed AND video is ready
    if (minTimePassed && videoReady && !hasTriggered.current && phase === 'swimming') {
      hasTriggered.current = true;
      setPhase('fadeOut');
      setTimeout(() => {
        setPhase('done');
        if (onReady) onReady();
      }, 800);
    }
  }, [minTimePassed, videoReady, phase, onReady]);

  // Fallback: if video takes too long (8s), reveal anyway
  useEffect(() => {
    const fallback = setTimeout(() => {
      if (!hasTriggered.current) {
        hasTriggered.current = true;
        setPhase('fadeOut');
        setTimeout(() => {
          setPhase('done');
          if (onReady) onReady();
        }, 800);
      }
    }, 8000);
    return () => clearTimeout(fallback);
  }, [onReady]);

  if (phase === 'done') return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#0a1a2e] transition-opacity duration-700 ${
        phase === 'fadeOut' ? 'opacity-0' : 'opacity-100'
      }`}
      data-testid="swimmer-loader"
    >
      {/* Water ripple background */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0" style={{
          background: 'radial-gradient(ellipse at 50% 80%, rgba(6, 182, 212, 0.15) 0%, transparent 70%)'
        }} />
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute h-px w-full opacity-20"
            style={{
              top: `${55 + i * 8}%`,
              background: 'linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.6) 50%, transparent 100%)',
              animation: `waterLine ${2 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Lane lines */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="absolute left-1/4 top-[45%] bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-400 to-transparent" />
        <div className="absolute left-1/2 top-[45%] bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-400 to-transparent" />
        <div className="absolute left-3/4 top-[45%] bottom-0 w-px bg-gradient-to-b from-transparent via-cyan-400 to-transparent" />
      </div>

      {/* Swimmer SVG Animation */}
      <div className="relative w-full max-w-lg h-32 mb-8">
        <svg
          className="swimmer-svg absolute"
          style={{
            animation: 'swimAcross 3s ease-in-out infinite',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
          width="80"
          height="40"
          viewBox="0 0 80 40"
          fill="none"
        >
          <ellipse cx="35" cy="20" rx="20" ry="6" fill="#06b6d4" opacity="0.9" />
          <circle cx="55" cy="18" r="6" fill="#06b6d4" />
          <g style={{ animation: 'strokeArm 0.6s ease-in-out infinite', transformOrigin: '40px 16px' }}>
            <path d="M40 16 Q50 4 60 12" stroke="#06b6d4" strokeWidth="3" fill="none" strokeLinecap="round" />
          </g>
          <g style={{ animation: 'kickLegs 0.4s ease-in-out infinite alternate', transformOrigin: '18px 22px' }}>
            <line x1="18" y1="22" x2="5" y2="16" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" />
            <line x1="18" y1="22" x2="5" y2="28" stroke="#06b6d4" strokeWidth="2.5" strokeLinecap="round" />
          </g>
          <circle cx="10" cy="14" r="1.5" fill="#22d3ee" opacity="0.6" style={{ animation: 'splash 0.8s ease-out infinite' }} />
          <circle cx="8" cy="22" r="1" fill="#22d3ee" opacity="0.5" style={{ animation: 'splash 0.8s ease-out infinite 0.2s' }} />
          <circle cx="12" cy="26" r="1.5" fill="#22d3ee" opacity="0.4" style={{ animation: 'splash 0.8s ease-out infinite 0.4s' }} />
        </svg>

        <div
          className="absolute top-1/2 -translate-y-1/2 h-1 rounded-full"
          style={{
            animation: 'waterTrail 3s ease-in-out infinite',
            background: 'linear-gradient(90deg, transparent, rgba(6,182,212,0.3), rgba(6,182,212,0.1))',
          }}
        />
      </div>

      {/* Logo + Loading Text */}
      <div className="relative text-center space-y-4">
        <img
          src="https://customer-assets.emergentagent.com/job_eb4b9bb9-81a9-4a45-a21f-b2856d0d9f8b/artifacts/g3ev7obu_Untitled%20design%20%2817%29.png"
          alt="Hurupu Pools"
          className="h-16 mx-auto"
          style={{ animation: 'fadeInUp 1s ease-out 0.5s both' }}
        />
        <div
          className="flex items-center justify-center gap-2 text-cyan-400/70 text-sm"
          style={{ animation: 'fadeInUp 1s ease-out 1s both' }}
        >
          <div className="flex gap-1">
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" style={{ animation: 'dotPulse 1.2s ease-in-out infinite' }} />
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" style={{ animation: 'dotPulse 1.2s ease-in-out 0.2s infinite' }} />
            <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full" style={{ animation: 'dotPulse 1.2s ease-in-out 0.4s infinite' }} />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes swimAcross {
          0% { left: -100px; }
          100% { left: calc(100% + 100px); }
        }
        @keyframes strokeArm {
          0%, 100% { transform: rotate(-15deg); }
          50% { transform: rotate(25deg); }
        }
        @keyframes kickLegs {
          0% { transform: rotate(-8deg); }
          100% { transform: rotate(8deg); }
        }
        @keyframes splash {
          0% { opacity: 0.6; transform: translate(0, 0) scale(1); }
          100% { opacity: 0; transform: translate(-8px, -4px) scale(0.3); }
        }
        @keyframes waterTrail {
          0% { left: 0; width: 0; }
          50% { left: 0; width: 50%; }
          100% { left: 100%; width: 0; }
        }
        @keyframes waterLine {
          0%, 100% { transform: translateX(-5%) scaleX(0.95); opacity: 0.15; }
          50% { transform: translateX(5%) scaleX(1.05); opacity: 0.25; }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes dotPulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export { SwimmerLoader };
